import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ChartPieIcon,
  BanknotesIcon,
  PhoneIcon,
  ClockIcon,
  DocumentCheckIcon,
  LightBulbIcon,
  AcademicCapIcon,
  NewspaperIcon,
  RocketLaunchIcon,
  EnvelopeIcon,
  MapPinIcon,
  CheckIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import { useRef, useEffect, useState } from 'react';

/* global TradingView */

const ComplianceLogo = ({ name }) => {
  const logos = {
    SEC: (
      <svg className="h-12 w-auto" viewBox="0 0 100 40" fill="currentColor">
        <path d="M20 10h60v20H20z"/>
        <text x="50" y="25" fontSize="14" fill="white" textAnchor="middle">SEC</text>
      </svg>
    ),
    FINRA: (
      <svg className="h-12 w-auto" viewBox="0 0 100 40" fill="currentColor">
        <path d="M20 10h60v20H20z"/>
        <text x="50" y="25" fontSize="14" fill="white" textAnchor="middle">FINRA</text>
      </svg>
    ),
    SIPC: (
      <svg className="h-12 w-auto" viewBox="0 0 100 40" fill="currentColor">
        <path d="M20 10h60v20H20z"/>
        <text x="50" y="25" fontSize="14" fill="white" textAnchor="middle">SIPC</text>
      </svg>
    ),
    SSL: (
      <svg className="h-12 w-auto" viewBox="0 0 100 40" fill="currentColor">
        <path d="M30 10l20 20M50 10l-20 20"/>
        <circle cx="50" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="2"/>
      </svg>
    )
  };

  return (
    <div className="text-gray-400 hover:text-gray-300 transition-colors">
      {logos[name]}
    </div>
  );
};

function InvestmentCalculator() {
  const [amount, setAmount] = useState('1000');
  const [plan, setPlan] = useState('standard');
  const [returns, setReturns] = useState(0);

  const calculateReturns = () => {
    const investmentAmount = parseFloat(amount);
    let totalReturn = 0;

    switch(plan) {
      case 'standard':
        if (investmentAmount >= 1000 && investmentAmount <= 4000) {
          totalReturn = investmentAmount * 10; // 10x return
        }
        break;
      case 'premium':
        if (investmentAmount >= 5000 && investmentAmount <= 9000) {
          totalReturn = investmentAmount * 15; // 15x return
        }
        break;
      case 'advanced':
        if (investmentAmount >= 10000) {
          totalReturn = investmentAmount * 20; // 20x return
        }
        break;
      default:
        totalReturn = 0;
    }

    setReturns(totalReturn);
  };

  useEffect(() => {
    calculateReturns();
  }, [amount, plan]);

  // Function to get minimum amount based on selected plan
  const getMinAmount = () => {
    switch(plan) {
      case 'standard':
        return 1000;
      case 'premium':
        return 5000;
      case 'advanced':
        return 10000;
      default:
        return 1000;
    }
  };

  // Function to get maximum amount based on selected plan
  const getMaxAmount = () => {
    switch(plan) {
      case 'standard':
        return 4000;
      case 'premium':
        return 9000;
      case 'advanced':
        return 999999999; // Effectively unlimited
      default:
        return 4000;
    }
  };

  // Update amount when plan changes to ensure it's within valid range
  useEffect(() => {
    const minAmount = getMinAmount();
    const maxAmount = getMaxAmount();
    const currentAmount = parseFloat(amount);

    if (currentAmount < minAmount) {
      setAmount(minAmount.toString());
    } else if (currentAmount > maxAmount && plan !== 'advanced') {
      setAmount(maxAmount.toString());
    }
  }, [plan]);

  return (
    <motion.div 
      className="bg-white rounded-xl shadow-lg p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Investment Calculator</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Investment Plan
          </label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="standard">Standard Plan ($1,000 - $4,000)</option>
            <option value="premium">Premium Plan ($5,000 - $9,000)</option>
            <option value="advanced">Advanced Plan ($10,000+)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Investment Amount ($)
          </label>
          <input
            type="number"
            min={getMinAmount()}
            max={plan === 'advanced' ? undefined : getMaxAmount()}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="pt-4">
          <div className="bg-primary-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Total Return</div>
            <div className="text-3xl font-bold text-primary-600">
              ${returns.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500 mt-2">
              21-day investment period
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Homepage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const hoverScale = {
    scale: 1.03,
    transition: {
      type: "spring",
      stiffness: 300
    }
  };

  const features = [
    {
      icon: ChartBarIcon,
      title: "Data-Driven Investments",
      description: "Advanced analytics and market insights guide our investment strategies"
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure & Regulated",
      description: "Your investments are protected by industry-leading security measures"
    },
    {
      icon: CurrencyDollarIcon,
      title: "Competitive Returns",
      description: "Historically proven track record of above-market returns"
    },
    {
      icon: ArrowTrendingUpIcon,
      title: "Growth Focused",
      description: "Strategic investment approach focused on long-term wealth creation"
    }
  ];

  const investmentOptions = [
    {
      title: "Stocks & Equities",
      description: "Diversified portfolio of high-performing stocks across multiple sectors",
      percentage: "40%",
      icon: ChartBarIcon
    },
    {
      title: "Real Estate",
      description: "Premium commercial and residential property investments",
      percentage: "25%",
      icon: BuildingOfficeIcon
    },
    {
      title: "Cryptocurrency",
      description: "Strategic positions in established and emerging digital assets",
      percentage: "20%",
      icon: GlobeAltIcon
    },
    {
      title: "Fixed Income",
      description: "Bonds and stable yield-generating instruments",
      percentage: "15%",
      icon: BanknotesIcon
    }
  ];

  const marketUpdates = [
    {
      title: "Market Analysis",
      date: "October 2023",
      description: "In-depth analysis of current market trends and opportunities."
    },
    {
      title: "Investment Strategy",
      date: "September 2023",
      description: "Updated portfolio allocation strategies for Q4 2023."
    },
    {
      title: "Economic Outlook",
      date: "August 2023",
      description: "Global economic indicators and their impact on investments."
    }
  ];

  const steps = [
    {
      icon: UserGroupIcon,
      title: "Create Account",
      description: "Quick and secure registration process"
    },
    {
      icon: DocumentCheckIcon,
      title: "Complete KYC",
      description: "Simple verification process"
    },
    {
      icon: BanknotesIcon,
      title: "Fund Account",
      description: "Multiple payment options available"
    },
    {
      icon: RocketLaunchIcon,
      title: "Start Investing",
      description: "Begin your investment journey"
    }
  ];

  const investmentOptionsRef = useRef(null);

  const scrollToInvestmentOptions = () => {
    investmentOptionsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Create a container for the ticker
    const tickerContainer = document.getElementById('tradingview-ticker');
    if (tickerContainer) {
      // Create a div with specific class required by TradingView
      const widgetContainer = document.createElement('div');
      widgetContainer.className = 'tradingview-widget-container';
      tickerContainer.appendChild(widgetContainer);

      // Load TradingView Widget Library
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
      script.async = true;
      script.type = 'text/javascript';
      script.innerHTML = `
        {
          "symbols": [
            {
              "proName": "BITSTAMP:BTCUSD",
              "title": "BTC/USD"
            },
            {
              "proName": "BITSTAMP:ETHUSD",
              "title": "ETH/USD"
            },
            {
              "proName": "BINANCE:BNBUSD",
              "title": "BNB/USD"
            },
            {
              "proName": "BINANCE:SOLUSD",
              "title": "SOL/USD"
            },
            {
              "proName": "BINANCE:ADAUSD",
              "title": "ADA/USD"
            }
          ],
          "showSymbolLogo": true,
          "colorTheme": "dark",
          "isTransparent": true,
          "displayMode": "adaptive",
          "locale": "en"
        }`;
      widgetContainer.appendChild(script);
    }

    // Load CryptoCompare price widget for desktop
    const priceScript = document.createElement('script');
    priceScript.src = "https://widgets.cryptocompare.com/serve/v3/coin/header?fsym=BTC&tsyms=USD,EUR,CNY,GBP";
    priceScript.async = true;
    document.getElementById('crypto-price-widget')?.appendChild(priceScript);

    // Load CryptoCompare price widget for mobile
    const mobilePriceScript = document.createElement('script');
    mobilePriceScript.src = "https://widgets.cryptocompare.com/serve/v3/coin/header?fsym=BTC&tsyms=USD,EUR&theme=dark";
    mobilePriceScript.async = true;
    document.getElementById('crypto-price-widget-mobile')?.appendChild(mobilePriceScript);

    // Load TradingView library for market widget
    const tvScript = document.createElement('script');
    tvScript.src = 'https://s3.tradingview.com/tv.js';
    tvScript.async = true;
    tvScript.onload = () => {
      // Initialize market widget after library is loaded
      if (window.TradingView) {
        new window.TradingView.widget({
          "container_id": "crypto-market-widget",
          "width": "100%",
          "height": "400",
          "defaultColumn": "overview",
          "screener_type": "crypto_mkt",
          "displayCurrency": "USD",
          "colorTheme": "dark",
          "locale": "en",
          "isTransparent": true,
          "showToolbar": false,
          "market": "crypto",
          "backgroundColor": "rgba(79, 70, 229, 0.1)",
          "gridLineColor": "rgba(79, 70, 229, 0.1)",
          "style": "1",
          "upColor": "#4F46E5",
          "downColor": "#7C3AED"
        });
      }
    };
    document.head.appendChild(tvScript);

    return () => {
      // Clean up widget containers
      ['crypto-market-widget', 'tradingview-ticker', 'crypto-price-widget', 'crypto-price-widget-mobile'].forEach(id => {
        const container = document.getElementById(id);
        if (container) {
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Add this new background div */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundColor: '#f8fafc',
          backgroundImage: `
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9)
          `,
          backgroundSize: '80px 140px',
          backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px',
          opacity: 0.1
        }}
      />

      {/* Wrap all existing content in a relative z-10 div */}
      <div className="relative z-10">
      <Navigation />

        {/* Hero Section - Update background opacity */}
        <section className="relative min-h-[80vh] flex flex-col md:flex-row bg-opacity-95">
        {/* Content */}
        <motion.div 
          className="flex-1 flex items-center justify-center p-6 sm:p-8 md:p-12 lg:p-16 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Mobile Background - Only visible on mobile */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-gray-900 md:hidden"></div>

          <div className="max-w-xl mx-auto text-center md:text-left relative">
            <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-bold text-white md:text-gray-900 mb-4 md:mb-6">
              Invest in Your Future with Confidence
            </h1>
            <p className="text-lg sm:text-xl md:text-xl text-gray-200 md:text-gray-600 mb-8">
              Professional investment solutions tailored to your goals. Join thousands of successful investors who trust QuickWealth Investment Group.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link to="/investment-plans">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-lg"
                >
                  View Investment Plans
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full inline-flex items-center justify-center px-8 py-4 border-2 border-white md:border-primary-600 text-lg font-medium rounded-md text-white md:text-primary-600 hover:bg-white/10 md:hover:bg-primary-50"
                >
                  Contact Us
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Hero Image - Hidden on mobile, visible on desktop */}
        <motion.div 
          className="hidden md:flex flex-1 relative h-[400px] md:h-auto bg-gray-900"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <img
            src="/images/top.jpg"
            alt="Investment Growth"
            className="absolute inset-0 w-full h-full object-cover opacity-90"
          />
          <div id="crypto-price-widget" className="absolute top-0 left-0 right-0 z-20"></div>
        </motion.div>

        {/* Crypto Price Widget for Mobile - Only visible on mobile */}
        <div className="md:hidden relative z-20 bg-black/30 p-4">
          <div id="crypto-price-widget-mobile" className="w-full"></div>
        </div>
      </section>

      {/* Calculator Section */}
        <section className="py-16 bg-gray-50/80 bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Investment Calculator */}
            <InvestmentCalculator />

            {/* Investment Overview */}
            <motion.div 
              className="bg-white rounded-xl shadow-lg p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Investment Overview</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-600">
                      <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                      Up to 20x Returns in 21 Days
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                      Welcome Bonus up to 5%
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                      Secure Investment Platform
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                      24/7 Expert Support
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Crypto Market Widget Section */}
        <section className="py-12 bg-white bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Live Crypto Market</h2>
            <p className="mt-4 text-lg text-gray-600">Track real-time cryptocurrency prices and market data</p>
          </div>
          <motion.div
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div id="crypto-market-widget" style={{ height: '400px' }}></div>
          </motion.div>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">Data provided by TradingView</p>
          </div>
        </div>
      </section>

      {/* Investment Plans Preview */}
        <section className="py-16 bg-gray-50/80 bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Investment Plans</h2>
            <p className="mt-4 text-lg text-gray-600">Choose the perfect investment plan for your financial goals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Standard Plan",
                price: "$1,000",
                returns: "$10,000",
                features: ["10x Profit in 21 days", "2% Welcome bonus", "Min: $1,000 - Max: $4,000", "24/7 Email Support"]
              },
              {
                name: "Premium Plan",
                price: "$5,000",
                returns: "$75,000",
                features: ["15x Profit in 21 days", "3% Welcome bonus", "Min: $5,000 - Max: $9,000", "Priority Support"],
                popular: true
              },
              {
                name: "Advanced Plan",
                price: "$10,000",
                returns: "$200,000",
                features: ["20x Profit in 21 days", "5% Welcome bonus", "Min: $10,000 - Max: $14,000", "VIP Benefits"]
              }
            ].map((plan, index) => (
              <motion.div
                key={index}
                className={`relative bg-white rounded-xl shadow-lg overflow-hidden ${
                  plan.popular ? 'ring-2 ring-primary-500' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary-500 text-white px-4 py-1 text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="flex flex-col gap-2 mb-6">
                    <div className="text-sm text-gray-500">Initial Investment</div>
                    <div className="text-3xl font-bold text-gray-900">{plan.price}</div>
                    <div className="text-sm text-gray-500">Monthly Returns</div>
                    <div className="text-2xl font-bold text-primary-600">{plan.returns}</div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/investment-plans">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-3 px-4 rounded-lg font-medium ${
                        plan.popular
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Get Started
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <motion.div 
          className="bg-primary-700/90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <p className="text-5xl font-extrabold text-white">$50M+</p>
              <p className="mt-2 text-white text-opacity-80">Assets Under Management</p>
            </motion.div>
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <p className="text-5xl font-extrabold text-white">20x</p>
              <p className="mt-2 text-white text-opacity-80">Investment Returns In 21 days</p>
            </motion.div>
            <motion.div 
              className="text-center"
              whileHover={{ scale: 1.05 }}
            >
              <p className="text-5xl font-extrabold text-white">500+</p>
              <p className="mt-2 text-white text-opacity-80">Happy Investors</p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-opacity-95">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="bg-white rounded-lg shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ y: -5 }}
            >
              <feature.icon className="h-12 w-12 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Investment Options Section */}
        <section ref={investmentOptionsRef} className="py-16 bg-white/80 bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Investment Options</h2>
            <p className="mt-4 text-lg text-gray-600">Diversified investment strategies for optimal returns</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentOptions.map((option, index) => (
              <motion.div
                key={option.title}
                className="bg-gray-50 rounded-lg p-6 shadow-lg"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <option.icon className="h-12 w-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                <div className="text-2xl font-bold text-primary-600">{option.percentage}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <motion.section 
          className="py-16 bg-gray-900/90 text-white bg-opacity-95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Why Choose Quickwealth Investment Group LLC</h2>
            <p className="mt-4 text-lg text-gray-300">Your success is our priority</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="text-center p-6"
              whileHover={{ scale: 1.05 }}
            >
              <ShieldCheckIcon className="h-12 w-12 mx-auto text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Security First</h3>
              <p className="text-gray-300">Bank-level security and regulatory compliance</p>
            </motion.div>
            <motion.div 
              className="text-center p-6"
              whileHover={{ scale: 1.05 }}
            >
              <ChartPieIcon className="h-12 w-12 mx-auto text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Expert Management</h3>
              <p className="text-gray-300">Professional portfolio management and rebalancing</p>
            </motion.div>
            <motion.div 
              className="text-center p-6"
              whileHover={{ scale: 1.05 }}
            >
              <UserGroupIcon className="h-12 w-12 mx-auto text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Dedicated Support</h3>
              <p className="text-gray-300">24/7 customer service and investment advice</p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Certificates Section */}
      <motion.section
        className="py-16 bg-white/90 bg-opacity-95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Licenses & Insurance</h2>
            <p className="mt-4 text-lg text-gray-600">Fully Licensed, Regulated, and Insured for Your Security</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* FCA Certificate */}
            <motion.div
              className="bg-white rounded-xl shadow-lg overflow-hidden"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="relative aspect-[4/3]">
                <img
                  src="/images/cert2.jpeg"
                  alt="FCA License Certificate"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 border-t border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900">Financial Conduct Authority (FCA)</h3>
                <p className="mt-2 text-gray-600">License No: 923365</p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Financial Management Services (FMS)
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Regulated in New York, USA
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Werla Insurance Certificate */}
            <motion.div
              className="bg-white rounded-xl shadow-lg overflow-hidden"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative aspect-[4/3]">
                <img
                  src="/images/cert1.jpeg"
                  alt="Werla Insurance Certificate"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 border-t border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900">Werla Insurance Coverage</h3>
                <p className="mt-2 text-gray-600">Registration No: I 90 763 781 563</p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Comprehensive Investment Protection
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Active Capital Insurance
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Performance Section */}
      <motion.section 
          className="py-16 bg-white/80 bg-opacity-95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Performance</h2>
            <p className="mt-4 text-lg text-gray-600">Consistent returns through market cycles</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-gray-50 rounded-lg p-8 shadow-lg"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Historical Returns</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">1 Year Return</span>
                  <span className="text-primary-600 font-bold">15.8%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">3 Year Average</span>
                  <span className="text-primary-600 font-bold">12.4%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">5 Year Average</span>
                  <span className="text-primary-600 font-bold">10.9%</span>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="bg-gray-50 rounded-lg p-8 shadow-lg"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Risk Management</h3>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                  Portfolio Diversification
                </li>
                <li className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                  Regular Rebalancing
                </li>
                <li className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                  Market Risk Analysis
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section 
          className="py-16 bg-gray-50/80 bg-opacity-95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">What Our Investors Say</h2>
            <p className="mt-4 text-lg text-gray-600">Join our community of successful investors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Michael Chen",
                since: "2021",
                testimonial: "The platform's advanced analytics and professional management have helped me achieve consistent returns. Their customer support is exceptional, always ready to assist."
              },
              {
                name: "Sarah Williams",
                since: "2020",
                testimonial: "I've tried several investment platforms, but Quickwealth Investment Group LLC stands out. The returns are impressive, and their risk management strategy gives me peace of mind."
              },
              {
                name: "David Rodriguez",
                since: "2022",
                testimonial: "Starting with the standard plan was the best financial decision I've made. The platform is intuitive, and the monthly returns have exceeded my expectations."
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg"
                whileHover={{ y: -5 }}
              >
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <UserGroupIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold">{testimonial.name}</h4>
                    <p className="text-gray-600">Investor since {testimonial.since}</p>
                  </div>
                </div>
                <p className="text-gray-600">"{testimonial.testimonial}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.div 
          className="bg-primary-600/90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Ready to Start Investing?
            </h2>
            <p className="mt-4 text-lg text-white text-opacity-80">
              Join thousands of successful investors who trust Quickwealth Investment Group LLC.
            </p>
            <motion.div 
              className="mt-8"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10"
              >
                Create Your Account
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions Section */}
        <section className="py-12 bg-white/80 bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              className="bg-primary-50 p-6 rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
            >
              <PhoneIcon className="h-8 w-8 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">24/7 Support</h3>
              <p className="text-gray-600">Our team is always here to help you</p>
              <Link to="/contact" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
                Contact Us →
              </Link>
            </motion.div>
            <motion.div
              className="bg-primary-50 p-6 rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
            >
              <ClockIcon className="h-8 w-8 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Market Hours</h3>
              <p className="text-gray-600">Track global market opening hours</p>
              <button className="text-primary-600 hover:text-primary-700 mt-4">
                View Schedule →
              </button>
            </motion.div>
            <motion.div
              className="bg-primary-50 p-6 rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
            >
              <LightBulbIcon className="h-8 w-8 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Investment Tips</h3>
              <p className="text-gray-600">Latest insights and strategies</p>
              <Link to="/resources" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
                Learn More →
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Getting Started Steps */}
      <motion.section 
          className="py-16 bg-gray-50/80 bg-opacity-95"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Start Investing in Minutes</h2>
            <p className="mt-4 text-lg text-gray-600">Simple steps to begin your investment journey</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                variants={itemVariants}
                whileHover={hoverScale}
              >
                <div className="bg-white rounded-lg p-6 shadow-lg h-full">
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center">
                    {index + 1}
                  </div>
                  <step.icon className="h-12 w-12 text-primary-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Market Updates Section */}
      <motion.section 
          className="py-16 bg-white/80 bg-opacity-95"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Latest Market Updates</h2>
            <p className="mt-4 text-lg text-gray-600">Stay informed with our expert analysis</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {marketUpdates.map((update, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-lg p-6 shadow-lg"
                variants={itemVariants}
                whileHover={hoverScale}
              >
                <NewspaperIcon className="h-8 w-8 text-primary-600 mb-4" />
                <span className="text-sm text-primary-600 font-medium">{update.date}</span>
                <h3 className="text-xl font-semibold my-2">{update.title}</h3>
                <p className="text-gray-600 mb-4">{update.description}</p>
                <button className="text-primary-600 hover:text-primary-700 font-medium">
                  Read More →
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Insights Section - With horizontal scroll on mobile */}
      <motion.section 
        className="py-16 bg-gray-900/95 text-white overflow-hidden"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Investment Insights</h2>
            <p className="mt-4 text-lg text-gray-300">Expand your knowledge with our expert analysis and insights</p>
          </div>
          {/* Add horizontal scroll container for mobile */}
          <div className="relative">
            <div className="flex overflow-x-auto pb-4 md:grid md:grid-cols-3 md:gap-8 -mx-4 px-4 snap-x snap-mandatory hide-scrollbar">
              {[
                {
                  title: "Understanding Cryptocurrency Markets",
                  description: "Learn about the fundamentals of cryptocurrency trading and market analysis",
                  video: "/vids/vid1.mp4",
                  thumbnail: "/images/btc.jpg"
                },
                {
                  title: "Investment Strategies for 2025",
                  description: "Discover proven strategies for maximizing your investment returns",
                  video: "/vids/vid2.mp4",
                  thumbnail: "/images/btc.jpg"
                },
                {
                  title: "Digital Asset Management",
                  description: "Master the art of managing and growing your digital asset portfolio",
                  video: "/vids/vid3.mp4",
                  thumbnail: "/images/btc.jpg"
                }
              ].map((insight, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl flex-shrink-0 w-[85vw] md:w-auto mx-2 first:ml-0 last:mr-0 snap-center"
                  variants={itemVariants}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <div className="aspect-w-16 aspect-h-9 relative group cursor-pointer">
                    <video 
                      className="w-full h-full object-cover"
                      playsInline
                      onClick={(e) => {
                        e.preventDefault();
                        const video = e.currentTarget;
                        if (video.paused) {
                          // Pause all other videos first
                          document.querySelectorAll('video').forEach(v => {
                            if (v !== video) v.pause();
                          });
                          // Then play this video
                          video.play().catch(err => console.log('Video playback error:', err));
                        } else {
                          video.pause();
                        }
                      }}
                    >
                      <source src={insight.video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div 
                      className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:opacity-0 transition-opacity duration-300"
                      onClick={(e) => {
                        e.preventDefault();
                        const video = e.currentTarget.previousSibling;
                        if (video.paused) {
                          video.play().catch(err => console.log('Video playback error:', err));
                        } else {
                          video.pause();
                        }
                      }}
                    >
                      <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                        <PlayIcon className="h-10 w-10 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2 text-primary-400">{insight.title}</h3>
                    <p className="text-gray-300 mb-4">{insight.description}</p>
                    <div className="flex items-center text-sm text-primary-400">
                      <PlayIcon className="h-5 w-5 mr-2" />
                      <span>Watch Now</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Add custom scrollbar styles */}
        <style jsx>{`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </motion.section>

      {/* Educational Resources Section */}
      <motion.section 
          className="py-16 bg-gray-900/90 text-white bg-opacity-95"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Investment Education</h2>
            <p className="mt-4 text-lg text-gray-300">Enhance your investment knowledge</p>
          </div>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
          >
            <motion.div
              className="bg-gray-800 rounded-lg p-6"
              variants={itemVariants}
              whileHover={hoverScale}
            >
              <AcademicCapIcon className="h-12 w-12 text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Investment Basics</h3>
              <p className="text-gray-300 mb-4">Learn the fundamentals of investing and portfolio management</p>
              <button className="text-primary-400 hover:text-primary-300">
                Start Learning →
              </button>
            </motion.div>
            <motion.div
              className="bg-gray-800 rounded-lg p-6"
              variants={itemVariants}
              whileHover={hoverScale}
            >
              <ChartPieIcon className="h-12 w-12 text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Market Analysis</h3>
              <p className="text-gray-300 mb-4">Master technical and fundamental analysis techniques</p>
              <button className="text-primary-400 hover:text-primary-300">
                Explore Topics →
              </button>
            </motion.div>
            <motion.div
              className="bg-gray-800 rounded-lg p-6"
              variants={itemVariants}
              whileHover={hoverScale}
            >
              <LightBulbIcon className="h-12 w-12 text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Strategy Guides</h3>
              <p className="text-gray-300 mb-4">Advanced investment strategies and portfolio optimization</p>
              <button className="text-primary-400 hover:text-primary-300">
                View Guides →
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Personal Dashboard Section */}
        <section className="py-16 bg-white/80 bg-opacity-95 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Personal Investment Dashboard</h2>
              <p className="text-lg text-gray-600 mb-8">
                Access your comprehensive investment portfolio anytime, anywhere. Track your investments, 
                monitor performance, and make informed decisions with real-time market data and 
                personalized insights.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Real-time portfolio tracking and analytics
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Personalized investment recommendations
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Secure transactions and portfolio management
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <span className="ml-2">Access Dashboard</span>
                  </motion.button>
                </Link>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center justify-center px-6 py-3 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
                  >
                    <span className="ml-2">Schedule Demo</span>
                  </motion.button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img
                src="/images/btc.jpg"
                alt="Investment Dashboard Interface"
                className="absolute inset-0 w-full h-full object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-sm font-medium mb-2">Live Portfolio Overview</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold">$23,647,582.21</span>
                  <span className="text-green-400 font-medium">+12.4%</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <motion.section 
          className="py-16 bg-primary-900/90 bg-opacity-95"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-lg text-primary-100 mb-8">
              Subscribe to our newsletter for the latest investment insights and market updates
            </p>
            <motion.div 
              className="max-w-md mx-auto"
              variants={itemVariants}
            >
              <form className="flex space-x-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <motion.button
                  className="bg-primary-600 text-white px-6 py-3 rounded-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Subscribe
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </motion.section>
      </div>

      <Footer />

      {/* Sticky Trading View Ticker */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-primary-600/95 border-t border-primary-700 shadow-lg backdrop-blur-sm h-[52px] md:h-[46px]">
        <div id="tradingview-ticker" className="h-full overflow-visible"></div>
      </div>
    </div>
  );
}

export default Homepage; 